import ApplicationController from "./application_controller"

// Controller for dynamic lists with Turbo Streams
// Usage:
// <div data-controller="turbo-list">
//   <div data-turbo-list-target="list">
//     <!-- List items -->
//   </div>
//   <button data-action="click->turbo-list#loadMore">Load more</button>
// </div>

export default class extends ApplicationController {
  static targets = [ "list", "loadMoreButton", "spinner" ]
  static values = {
    url: String,
    page: { type: Number, default: 1 },
    loading: { type: Boolean, default: false }
  }

  connect() {
    this.setupInfiniteScroll()
  }

  disconnect() {
    this.teardownInfiniteScroll()
  }

  async loadMore(event) {
    event?.preventDefault()

    if (this.loadingValue) return

    this.loadingValue = true
    this.showSpinner()

    try {
      const response = await fetch(`${this.urlValue}?page=${this.pageValue + 1}`, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })

      if (response.ok) {
        const html = await response.text()

        // Turbo will automatically handle the response if it is a Turbo Stream
        if (response.headers.get('Content-Type')?.includes('turbo-stream')) {
          Turbo.renderStreamMessage(html)
        } else {
          // Or add the HTML directly
          this.listTarget.insertAdjacentHTML('beforeend', html)
        }

        this.pageValue += 1
        this.dispatch("turbo-list:loaded", { page: this.pageValue })
      } else {
        this.handleError(response)
      }
    } catch (error) {
      this.dispatch("turbo-list:error", { error: error.message })
    } finally {
      this.loadingValue = false
      this.hideSpinner()
    }
  }

  setupInfiniteScroll() {
    if (!this.hasLoadMoreButtonTarget) return

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.loadingValue) {
            this.loadMore()
          }
        })
      },
      { threshold: 0.1 }
    )

    this.intersectionObserver.observe(this.loadMoreButtonTarget)
  }

  teardownInfiniteScroll() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove('hidden')
    }

    if (this.hasLoadMoreButtonTarget) {
      this.loadMoreButtonTarget.disabled = true
      this.loadMoreButtonTarget.classList.add('opacity-50')
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add('hidden')
    }

    if (this.hasLoadMoreButtonTarget) {
      this.loadMoreButtonTarget.disabled = false
      this.loadMoreButtonTarget.classList.remove('opacity-50')
    }
  }

  handleError(response) {
    console.error(`Failed to load more items: ${response.status}`)

    const errorMessage = document.createElement('div')
    errorMessage.className = 'alert alert-danger mt-3'
    errorMessage.textContent = 'Failed to load more items'

    this.listTarget.appendChild(errorMessage)

    // Remove the message after 5 seconds
    setTimeout(() => errorMessage.remove(), 5000)
  }

  // Method to remove an item from the list (useful with Turbo Streams)
  removeItem(event) {
    const item = event.currentTarget.closest('[data-turbo-list-item]')
    if (item) {
      item.remove()
      this.dispatch("turbo-list:item-removed")
    }
  }
}
